<template>
  <div class="p-field">
    <label v-if="label" :for="id" :class="{ 'p-fixed': labelStyle === 'fixed' }">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <div
      :id="id"
      class="p-inputgroup"
      :class="{
        'p-inputgroup-sm': size === 'small',
        'p-inputgroup-lg': size === 'large',
        'p-disabled': disabled,
        'p-invalid': invalid
      }"
      :aria-label="props['aria-label']"
      :aria-labelledby="props['aria-labelledby']"
      :aria-describedby="ariaDescribedBy"
      :aria-required="required"
      :aria-invalid="invalid"
    >
      <slot name="addon-left">
        <InputGroupAddon v-if="startAddon || startIcon">
          <i v-if="startIcon" :class="startIcon"></i>
          <span v-else v-text="startAddon"></span>
        </InputGroupAddon>
      </slot>

      <slot></slot>

      <slot name="addon-right">
        <InputGroupAddon v-if="endAddon || endIcon">
          <i v-if="endIcon" :class="endIcon"></i>
          <span v-else v-text="endAddon"></span>
        </InputGroupAddon>
      </slot>
    </div>

    <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
    <small
      v-for="(error, index) in validationErrors"
      :key="index"
      :id="`${id}-error-${index}`"
      class="p-error block"
    >
      {{ error }}
    </small>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';

defineOptions({
  name: 'ONEInputGroup'
});

interface ValidationRule {
  validator: (value: unknown) => boolean | Promise<boolean>;
  message: string;
}

const props = withDefaults(defineProps<{
  modelValue?: unknown;
  label?: string;
  labelStyle?: 'fixed' | 'float';
  size?: 'small' | 'large';
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  helperText?: string;
  startAddon?: string;
  endAddon?: string;
  startIcon?: string;
  endIcon?: string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  validationRules?: ValidationRule[];
}>(), {
  id: () => `one-inputgroup-${Math.random().toString(36).substr(2, 9)}`,
  validationRules: () => []
});

const emit = defineEmits<{
  (e: 'validation-error', errors: string[]): void;
  (e: 'validation-success'): void;
}>();

const isDirty = ref(false);
const isValidating = ref(false);
const validationErrors = ref<string[]>([]);

const helperTextId = computed(() => `${props.id}-helper`);

const ariaDescribedBy = computed(() => {
  const ids = [];
  if (props.helperText) ids.push(helperTextId.value);
  validationErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`));
  return ids.join(' ');
});

const isValid = computed(() => validationErrors.value.length === 0);

const validate = async (): Promise<boolean> => {
  isValidating.value = true;
  try {
    const errors: string[] = [];
    for (const rule of props.validationRules) {
      const isValid = await rule.validator(props.modelValue);
      if (!isValid) {
        errors.push(rule.message);
      }
    }
    validationErrors.value = errors;

    if (errors.length > 0) {
      emit('validation-error', errors);
      return false;
    }

    emit('validation-success');
    return true;
  } finally {
    isValidating.value = false;
  }
};

defineExpose({
  validate,
  validationErrors,
  isDirty,
  isValidating,
  isValid
});
</script>
